import { useEffect, useMemo, useRef, useState } from "react";

import { logError } from "@/utils/logger";

import { peaksFromBuffer } from "../utils/voice";

export interface UseAudioPlayerReturn {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  toggle: () => void;
  seek: (ratio: number) => void;
  progress: number;
  peaks: number[];
  rate: number;
  setRate: (rate: number) => void;
}

interface Waveform {
  peaks: number[];
  duration: number;
}

/* One element plays at a time, including a recording preview in the same page. */
let claimedAudio: HTMLAudioElement | null = null;

export function claimAudio(audio: HTMLAudioElement) {
  if (claimedAudio && claimedAudio !== audio) claimedAudio.pause();
  claimedAudio = audio;
}

export function releaseAudio(audio: HTMLAudioElement) {
  if (claimedAudio === audio) claimedAudio = null;
}

const waveformCache = new Map<string, Waveform>();
const inflightDecodes = new Map<string, Promise<Waveform>>();
const playbackUrls = new Map<string, string>();
const inflightPrepares = new Map<string, Promise<void>>();

let sharedContext: AudioContext | null = null;

function audioContext() {
  if (!sharedContext || sharedContext.state === "closed") {
    sharedContext = new AudioContext();
  }
  return sharedContext;
}

const DECODE_TIMEOUT_MS = 8_000;

/*
 * MediaRecorder WebM often has no duration header, so `decodeAudioData` can hang
 * and `audio.duration` stays `Infinity`. Cache peaks + length once decoding finishes.
 * A blob URL can fail while the recording is still being finalised, so callers retry.
 */
function startDecode(url: string, bytes: ArrayBuffer): Promise<Waveform> {
  const cached = waveformCache.get(url);
  if (cached) return Promise.resolve(cached);

  const pending = inflightDecodes.get(url);
  if (pending) return pending;

  const task = (async () => {
    let timer = 0;
    const buffer = await Promise.race([
      audioContext().decodeAudioData(bytes.slice(0)),
      new Promise<AudioBuffer>((_, reject) => {
        timer = window.setTimeout(
          () => reject(new Error("audio_decode_timeout")),
          DECODE_TIMEOUT_MS,
        );
      }),
    ]).finally(() => window.clearTimeout(timer));

    const waveform = {
      peaks: peaksFromBuffer(buffer),
      duration: buffer.duration,
    };
    waveformCache.set(url, waveform);
    return waveform;
  })();

  inflightDecodes.set(url, task);
  task.finally(() => {
    inflightDecodes.delete(url);
  });
  return task;
}

function decodeWaveform(url: string): Promise<Waveform> {
  const cached = waveformCache.get(url);
  if (cached) return Promise.resolve(cached);

  const pending = inflightDecodes.get(url);
  if (pending) return pending;

  return (async () => {
    const response = await fetch(url, { credentials: "include" });
    if (!response.ok) throw new Error("audio_fetch_failed");
    const bytes = await response.arrayBuffer();
    rememberPlayback(url, bytes, response.headers.get("content-type"));
    return startDecode(url, bytes);
  })();
}

function rememberPlayback(
  url: string,
  bytes: ArrayBuffer,
  contentType: string | null,
) {
  if (playbackUrls.has(url)) return;
  playbackUrls.set(
    url,
    URL.createObjectURL(
      new Blob([bytes], { type: contentType || "audio/ogg" }),
    ),
  );
}

/** Download once, then play from the cached blob. Peaks decode in the background. */
export function prepareAudio(url: string): Promise<void> {
  if (playbackUrls.has(url)) return Promise.resolve();

  const pending = inflightPrepares.get(url);
  if (pending) return pending;

  const task = (async () => {
    const response = await fetch(url, { credentials: "include" });
    if (!response.ok) throw new Error("audio_fetch_failed");
    const bytes = await response.arrayBuffer();
    rememberPlayback(url, bytes, response.headers.get("content-type"));
    void startDecode(url, bytes).catch((error: unknown) => {
      logError(error, { context: "audio_decode" });
    });
  })();

  inflightPrepares.set(url, task);
  task.finally(() => {
    inflightPrepares.delete(url);
  });
  return task;
}

export function isAudioPrepared(url: string) {
  return playbackUrls.has(url);
}

/** Blob URL after `prepareAudio`, otherwise the remote URL. */
export function preparedAudioUrl(url: string) {
  return playbackUrls.get(url) ?? url;
}

export function loadWaveform(
  url: string,
  onReady: (waveform: Waveform) => void,
  attempts = 3,
) {
  const cached = waveformCache.get(url);
  if (cached) {
    onReady(cached);
    return () => {};
  }

  let cancelled = false;
  let timer: number | null = null;

  const attempt = (remaining: number) => {
    decodeWaveform(url)
      .then((waveform) => {
        if (!cancelled) onReady(waveform);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (remaining <= 1) {
          logError(error, { context: "audio_decode" });
          return;
        }
        timer = window.setTimeout(() => attempt(remaining - 1), 400);
      });
  };

  attempt(attempts);

  return () => {
    cancelled = true;
    if (timer !== null) window.clearTimeout(timer);
  };
}

export function knownDuration(src: string, audio: HTMLAudioElement) {
  if (Number.isFinite(audio.duration) && audio.duration > 0)
    return audio.duration;
  return waveformCache.get(src)?.duration ?? 0;
}

/*
 * Playback uses an <audio> element. AudioBufferSourceNode raises pitch when
 * `playbackRate` changes; HTMLMediaElement keeps the original pitch.
 */
export function useAudioPlayer(
  src: string | null,
  decoded: AudioBuffer | null = null,
): UseAudioPlayerReturn {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [peaks, setPeaks] = useState<number[]>([]);
  const [rate, setRateState] = useState(1);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const rateRef = useRef(1);
  const srcRef = useRef(src);
  const positionRef = useRef(0);
  const bufferPlay = useRef<{
    node: AudioBufferSourceNode;
    startedAt: number;
    offset: number;
  } | null>(null);
  srcRef.current = src;
  const decodedRef = useRef(decoded);
  decodedRef.current = decoded;
  const copiedRef = useRef<{ from: AudioBuffer; to: AudioBuffer } | null>(null);
  const playGen = useRef(0);

  const stopBuffer = () => {
    const play = bufferPlay.current;
    if (!play) return positionRef.current;
    const elapsed = Math.max(0, audioContext().currentTime - play.startedAt);
    const length = play.node.buffer?.duration ?? play.offset + elapsed;
    const position = Math.min(play.offset + elapsed, length);
    play.node.onended = null;
    try {
      play.node.stop();
    } catch {
      /* already stopped */
    }
    play.node.disconnect();
    bufferPlay.current = null;
    positionRef.current = position;
    return position;
  };

  const playbackBuffer = (recorded: AudioBuffer, context: AudioContext) => {
    if (copiedRef.current?.from === recorded) return copiedRef.current.to;
    const copy = context.createBuffer(
      recorded.numberOfChannels,
      recorded.length,
      recorded.sampleRate,
    );
    for (let channel = 0; channel < recorded.numberOfChannels; channel += 1) {
      copy.copyToChannel(recorded.getChannelData(channel), channel);
    }
    copiedRef.current = { from: recorded, to: copy };
    return copy;
  };

  const startFrom = (from: number) => {
    const recorded = decodedRef.current;
    if (!recorded) return false;

    const gen = (playGen.current += 1);
    const audio = audioRef.current;
    if (audio && !audio.paused) audio.pause();
    stopBuffer();

    const context = audioContext();
    const buffer = playbackBuffer(recorded, context);
    const node = context.createBufferSource();
    node.buffer = buffer;
    node.connect(context.destination);
    const start =
      from >= buffer.duration - 0.08
        ? 0
        : Math.max(0, Math.min(from, Math.max(0, buffer.duration - 0.05)));
    node.onended = () => {
      if (playGen.current !== gen) return;
      if (bufferPlay.current?.node !== node) return;
      bufferPlay.current = null;
      positionRef.current = buffer.duration;
      setCurrentTime(buffer.duration);
      setIsPlaying(false);
    };

    const begin = () => {
      if (playGen.current !== gen) {
        node.disconnect();
        return;
      }
      node.start(0, start);
      bufferPlay.current = {
        node,
        startedAt: context.currentTime,
        offset: start,
      };
      positionRef.current = start;
      setCurrentTime(start);
      setIsPlaying(true);
    };

    if (context.state === "running") begin();
    else void context.resume().then(begin);

    positionRef.current = start;
    setCurrentTime(start);
    setIsPlaying(true);
    return true;
  };

  useEffect(() => {
    if (!src) {
      setDuration(0);
      setCurrentTime(0);
      setIsPlaying(false);
      setPeaks([]);
      return;
    }

    setDuration(0);
    setCurrentTime(0);
    setIsPlaying(false);

    const audio = new Audio();
    audio.preload = "auto";
    audio.src = src;
    audio.playbackRate = rateRef.current;
    audio.preservesPitch = true;
    audioRef.current = audio;
    positionRef.current = 0;

    const publishDuration = () => {
      const length = knownDuration(src, audio);
      if (length > 0) setDuration(length);
    };

    const onPlay = () => {
      if (bufferPlay.current) return;
      setIsPlaying(true);
      claimAudio(audio);
    };

    const onEnded = () => {
      if (bufferPlay.current) return;
      setIsPlaying(false);
      positionRef.current = knownDuration(src, audio) || audio.currentTime;
      setCurrentTime(positionRef.current);
      releaseAudio(audio);
    };

    const onPause = () => {
      if (bufferPlay.current) return;
      setIsPlaying(false);
      positionRef.current = audio.currentTime;
      setCurrentTime(audio.currentTime);
    };

    audio.addEventListener("loadedmetadata", publishDuration);
    audio.addEventListener("durationchange", publishDuration);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);

    const stopWaveform = loadWaveform(src, (waveform) => {
      if (decodedRef.current) return;
      setPeaks(waveform.peaks);
      if (waveform.duration > 0) {
        setDuration((current) => (current > 0 ? current : waveform.duration));
      }
    });

    return () => {
      stopBuffer();
      stopWaveform();
      audio.removeEventListener("loadedmetadata", publishDuration);
      audio.removeEventListener("durationchange", publishDuration);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      if (audioRef.current === audio) audioRef.current = null;
      releaseAudio(audio);
    };
  }, [src]);

  useEffect(() => {
    if (!isPlaying) return;

    let frame = 0;
    const tick = () => {
      const play = bufferPlay.current;
      if (play) {
        const length = play.node.buffer?.duration ?? 0;
        const position =
          play.offset +
          Math.max(0, audioContext().currentTime - play.startedAt);
        if (length > 0 && position >= length) {
          stopBuffer();
          positionRef.current = length;
          setCurrentTime(length);
          setIsPlaying(false);
          return;
        }
        setCurrentTime(position);
      } else if (audioRef.current && !audioRef.current.paused) {
        setCurrentTime(audioRef.current.currentTime);
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [isPlaying]);

  const toggle = () => {
    if (bufferPlay.current) {
      setCurrentTime(stopBuffer());
      setIsPlaying(false);
      return;
    }

    const audio = audioRef.current;
    const source = srcRef.current;
    const length =
      decodedRef.current?.duration ||
      (source && audio ? knownDuration(source, audio) : 0);
    const from =
      length > 0 && positionRef.current >= length - 0.08
        ? 0
        : positionRef.current;
    if (startFrom(from)) return;

    if (!audio) return;
    positionRef.current = from;

    if (audio.paused) {
      void audio.play().catch((error: unknown) => {
        logError(error, { context: "audio_playback" });
      });
      return;
    }

    audio.pause();
  };

  const seek = (ratio: number) => {
    const buffer = decodedRef.current;
    const audio = audioRef.current;
    const source = srcRef.current;
    const length =
      buffer?.duration || (source && audio ? knownDuration(source, audio) : 0);
    if (length <= 0) return;

    const next = Math.max(0, Math.min(1, ratio)) * length;
    positionRef.current = next;
    setDuration((current) => (current > 0 ? current : length));
    setCurrentTime(next);
    startFrom(next);
  };

  const setRate = (next: number) => {
    if (next === rateRef.current) return;
    rateRef.current = next;
    setRateState(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const decodedPeaks = useMemo(
    () => (decoded ? peaksFromBuffer(decoded) : null),
    [decoded],
  );
  const shownDuration = decoded?.duration ?? duration;
  const progress =
    shownDuration > 0 ? Math.min(1, currentTime / shownDuration) : 0;

  return {
    isPlaying,
    currentTime,
    duration: shownDuration,
    toggle,
    seek,
    progress,
    peaks: decodedPeaks ?? peaks,
    rate,
    setRate,
  };
}

/** Peaks and duration for a bubble. Pass `enabled` so off-screen voices are not decoded. */
export function useVoicePeaks(
  url: string | null,
  enabled = true,
): { peaks: number[]; duration: number } {
  const [peaks, setPeaks] = useState<number[]>([]);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    setPeaks([]);
    setDuration(0);
  }, [url]);

  useEffect(() => {
    if (!url || !enabled) return;
    return loadWaveform(url, (waveform) => {
      setPeaks(waveform.peaks);
      setDuration(waveform.duration);
    });
  }, [url, enabled]);

  return { peaks, duration };
}
